import { useState } from 'react'
import type {
  DocFolderEntry,
  DocFolderSnapshot,
  DocumentSnapshot,
  FileSnapshot,
  FolderSnapshot,
  SerializedTable,
  SerializedTask,
  SerializedWidget,
  ShareSnapshot,
  TaskSnapshot,
  WidgetSnapshot
} from './types'
import { SafeHtml } from './SafeHtml'
import { safeCssColor, safeHref, safeImageSrc } from './safeUrl'
import { cellText, unnamedColumns } from './tableCells'

// Polymorphic renderer for every share kind. Each rendering keeps the same
// visual language — glass cards, deep navy ground, violet accent on titles +
// status pills — so a shared folder, task, widget, document and file all feel
// like part of the same product.

export default function SnapshotView({
  snapshot,
  blobUrl
}: {
  snapshot: ShareSnapshot
  // For a raw-file share: the public URL its bytes are served from
  // (`${apiBase}/share/<token>/blob`). Undefined for every other kind.
  blobUrl?: string
}): JSX.Element {
  switch (snapshot.kind) {
    case 'folder':
      return <FolderView s={snapshot} />
    case 'task':
      return <TaskView s={snapshot} />
    case 'widget':
      return <WidgetView s={snapshot} />
    case 'document':
      return <DocumentDocView s={snapshot} />
    case 'docfolder':
      return <DocFolderViewer s={snapshot} />
    case 'file':
      return <FileView s={snapshot} blobUrl={blobUrl} />
    default:
      return <UnknownView />
  }
}

// A raw Drive file shared by link. Images, PDFs, video and audio preview inline
// from the token-hosted blob URL; anything else offers a download. When the file
// was too large to host publicly (hosted === false) we say so honestly rather
// than showing a broken embed.
function FileView({ s, blobUrl }: { s: FileSnapshot; blobUrl?: string }): JSX.Element {
  const mime = s.mimeType || ''
  const canPreview = !!blobUrl && s.hosted
  const sizeLabel = formatBytes(s.sizeBytes)
  return (
    <div className="space-y-4">
      <div className="glass-card rounded-2xl p-6">
        <div className="text-[10px] uppercase tracking-[0.18em] text-accent font-semibold mb-1">
          {(s.ext || 'file').toUpperCase()} file
        </div>
        <h1 className="text-2xl font-semibold text-stone-100 mb-1 break-words">{s.name}</h1>
        <div className="text-[11px] text-stone-500">
          {mime || 'file'}{sizeLabel ? ` · ${sizeLabel}` : ''}
        </div>
      </div>

      {!s.hosted ? (
        <div className="glass-card rounded-xl p-5 text-[13px] text-stone-300">
          This file is too large to preview or download over a public link
          {sizeLabel ? ` (${sizeLabel})` : ''}. Ask the sender to share it with you inside Plexii instead.
        </div>
      ) : !blobUrl ? (
        <div className="glass-card rounded-xl p-5 text-[13px] text-stone-300">
          The file could not be loaded. The link may be from a newer version, or the file is still uploading.
        </div>
      ) : (
        <div className="glass-card rounded-2xl overflow-hidden">
          {canPreview && mime.startsWith('image/') && (
            <img src={blobUrl} alt={s.name} className="w-full max-h-[70vh] object-contain bg-black/20" />
          )}
          {canPreview && mime === 'application/pdf' && (
            <iframe src={blobUrl} title={s.name} className="w-full h-[75vh] bg-white" />
          )}
          {canPreview && mime.startsWith('video/') && (
            <video src={blobUrl} controls className="w-full max-h-[70vh] bg-black" />
          )}
          {canPreview && mime.startsWith('audio/') && (
            <div className="p-5">
              <audio src={blobUrl} controls className="w-full" />
            </div>
          )}
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-white/[0.06]">
            <span className="text-[12px] text-stone-400 truncate">{s.name}</span>
            <a
              href={`${blobUrl}${blobUrl.includes('?') ? '&' : '?'}download=1`}
              className="btn-primary !py-1.5 !px-3 !text-[12px] shrink-0"
              download={s.name}
            >
              Download
            </a>
          </div>
        </div>
      )}
    </div>
  )
}

function formatBytes(b: number): string {
  if (!b || b <= 0) return ''
  if (b < 1024) return `${b} B`
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`
  if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`
  return `${(b / (1024 * 1024 * 1024)).toFixed(1)} GB`
}

// A shared Drive folder: a list of its documents (and nested folders); clicking a
// document expands its read-only preview inline.
function DocFolderViewer({ s }: { s: DocFolderSnapshot }): JSX.Element {
  const total = countDocs(s.items)
  return (
    <div className="space-y-4">
      <div className="glass-card rounded-2xl p-6">
        <div className="text-[10px] uppercase tracking-[0.18em] text-accent font-semibold mb-1">Folder</div>
        <h1 className="text-2xl font-semibold text-stone-100 mb-1">{s.name}</h1>
        <div className="text-[11px] text-stone-500">
          {total} document{total === 1 ? '' : 's'}
        </div>
      </div>
      <div className="space-y-2">
        {s.items.length === 0 ? (
          <div className="glass-card rounded-xl p-4 text-[13px] text-stone-400">This folder is empty.</div>
        ) : (
          s.items.map((it, i) => <DocFolderRow key={i} entry={it} depth={0} />)
        )}
      </div>
    </div>
  )
}

function countDocs(items: DocFolderEntry[]): number {
  return items.reduce((n, it) => n + (it.type === 'document' ? 1 : countDocs(it.items ?? [])), 0)
}

function DocFolderRow({ entry, depth }: { entry: DocFolderEntry; depth: number }): JSX.Element {
  const [open, setOpen] = useState(false)
  const pad = { paddingLeft: `${depth * 14}px` }
  if (entry.type === 'folder') {
    return (
      <div>
        <div className="flex items-center gap-2 px-3 py-2 text-[13px] text-stone-300" style={pad}>
          <span className="text-accent">▸</span>
          <span className="font-medium">{entry.name}</span>
        </div>
        <div className="space-y-2">
          {(entry.items ?? []).map((it, i) => (
            <DocFolderRow key={i} entry={it} depth={depth + 1} />
          ))}
        </div>
      </div>
    )
  }
  const label =
    entry.docType === 'sheet'
      ? 'Spreadsheet'
      : entry.docType === 'slides'
        ? 'Slides'
        : entry.docType === 'map'
          ? 'Map'
          : 'Document'
  return (
    <div className="glass-card rounded-xl overflow-hidden" style={pad}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-white/[0.03]"
      >
        <span className="text-[10px] uppercase tracking-[0.12em] text-stone-500 font-semibold">{label}</span>
        <span className="text-[14px] text-stone-100 font-medium">{entry.name}</span>
        <span className="ml-auto text-stone-500 text-[12px]">{open ? 'Hide' : 'View'}</span>
      </button>
      {open && entry.html && (
        <SafeHtml
          className="office-doc overflow-x-auto text-stone-200 text-[14px] leading-relaxed px-4 pb-4 border-t border-white/[0.06] pt-3"
          html={entry.html}
        />
      )}
    </div>
  )
}

// Office document (doc / sheet / slides / map) — the body was pre-rendered to
// HTML on the desktop, but it arrives from another user over a public link, so
// it is rendered only through the allowlist sanitiser (src/sanitizeHtml.ts).
function DocumentDocView({ s }: { s: DocumentSnapshot }): JSX.Element {
  const label =
    s.docType === 'sheet'
      ? 'Spreadsheet'
      : s.docType === 'slides'
        ? 'Slides'
        : s.docType === 'map'
          ? 'Map'
          : 'Document'
  return (
    <div className="glass-card rounded-2xl p-6">
      <div className="text-[10px] uppercase tracking-[0.18em] text-accent font-semibold mb-1">{label}</div>
      <h1 className="text-2xl font-semibold text-stone-100 mb-3">{s.title}</h1>
      {/* overflow-x-auto: the box clips its content (paint containment), so a
          sheet wider than the card scrolls inside it instead of being cut off. */}
      <SafeHtml className="office-doc overflow-x-auto text-stone-200 text-[14px] leading-relaxed" html={s.html} />
    </div>
  )
}

function FolderView({ s }: { s: FolderSnapshot }): JSX.Element {
  return (
    <div className="space-y-4">
      <div className="glass-card rounded-2xl p-6">
        <div className="text-[10px] uppercase tracking-[0.18em] text-accent font-semibold mb-1">
          Folder
        </div>
        <h1 className="text-2xl font-semibold text-stone-100 mb-2">
          {s.folder.title}
        </h1>
        {s.folder.description && (
          <p className="text-[14px] text-stone-300 leading-relaxed">
            {s.folder.description}
          </p>
        )}
        <div className="mt-3 text-[11px] text-stone-500">
          {s.tasks.length} task{s.tasks.length === 1 ? '' : 's'}
        </div>
      </div>
      <div className="space-y-3">
        {s.tasks.map((t) => (
          <TaskCard key={t.id} t={t} />
        ))}
      </div>
    </div>
  )
}

function TaskView({ s }: { s: TaskSnapshot }): JSX.Element {
  return (
    <div className="space-y-4">
      <div className="glass-card rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-2">
          <div className="text-[10px] uppercase tracking-[0.18em] text-accent font-semibold">
            Task
          </div>
          <StatusPill status={s.task.status} />
          {s.task.dueDate && (
            <DueChip due={s.task.dueDate} done={s.task.status === 'done'} />
          )}
        </div>
        <h1 className="text-2xl font-semibold text-stone-100 mb-2">
          {s.task.title}
        </h1>
        {s.task.description && (
          <p className="text-[14px] text-stone-300 leading-relaxed">
            {s.task.description}
          </p>
        )}
      </div>
      {s.task.widgets.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-stone-400 font-semibold mb-2 px-2">
            Desk objects
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {s.task.widgets.map((w) => (
              <WidgetCard key={w.id} w={w} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function WidgetView({ s }: { s: WidgetSnapshot }): JSX.Element {
  return (
    <div className="glass-card rounded-2xl p-6">
      <div className="text-[10px] uppercase tracking-[0.18em] text-accent font-semibold mb-1">
        Desk object · {s.widget.kind}
      </div>
      {s.widget.title && (
        <h1 className="text-xl font-semibold text-stone-100 mb-3">
          {s.widget.title}
        </h1>
      )}
      <WidgetContent w={s.widget} large />
    </div>
  )
}

function UnknownView(): JSX.Element {
  return (
    <div className="glass-card rounded-2xl p-6">
      <p className="text-[13px] text-stone-300">
        This share format is newer than this viewer can read. Refresh the page or
        update Plexii to view it.
      </p>
    </div>
  )
}

// ── Task card (used inside a folder render) ─────────────────────────────

function TaskCard({ t }: { t: SerializedTask }): JSX.Element {
  return (
    <div className="glass-card rounded-xl p-4 hover:translate-y-[-1px] transition-transform">
      <div className="flex items-center gap-2 mb-1">
        <StatusPill status={t.status} />
        {t.dueDate && <DueChip due={t.dueDate} done={t.status === 'done'} />}
      </div>
      <h2 className="text-[15px] font-medium text-stone-100">{t.title}</h2>
      {t.description && (
        <p className="text-[12px] text-stone-400 mt-1 line-clamp-3">
          {t.description}
        </p>
      )}
      {t.widgets.length > 0 && (
        <div className="mt-2 text-[11px] text-stone-500">
          {t.widgets.length} object{t.widgets.length === 1 ? '' : 's'}
        </div>
      )}
    </div>
  )
}

// ── Widget card ─────────────────────────────────────────────────────────

function WidgetCard({ w }: { w: SerializedWidget }): JSX.Element {
  return (
    <div className="glass-card rounded-xl p-3 overflow-hidden">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.12em] text-stone-500 font-semibold mb-2">
        <span>{w.kind}</span>
        {w.title && <span className="text-stone-300 normal-case">— {w.title}</span>}
      </div>
      <WidgetContent w={w} large={false} />
    </div>
  )
}

function WidgetContent({ w, large }: { w: SerializedWidget; large: boolean }): JSX.Element {
  // Sticky / note / markdown / page — render text directly. For 'page' we
  // try to parse the content as Tiptap JSON and extract a flat string; if
  // it parses, we render it; if not, render the raw content as preformatted.
  if (
    w.kind === 'sticky' ||
    w.kind === 'note' ||
    w.kind === 'markdown' ||
    w.kind === 'page'
  ) {
    const text = extractText(w.content)
    if (!text.trim()) {
      return <div className="text-[12px] text-stone-500 italic">(empty)</div>
    }
    return (
      <div
        className={`whitespace-pre-wrap text-stone-200 ${large ? 'text-[14px]' : 'text-[12px]'}`}
      >
        {text}
      </div>
    )
  }

  // Table — render the inlined schema + rows.
  if (w.kind === 'table' && w.tableSnapshot) {
    return <TablePreview t={w.tableSnapshot} />
  }

  // WebView kinds — show the URL (the actual page can't be embedded in
  // an iframe due to most sites blocking it with X-Frame-Options).
  if (
    w.kind === 'webview' ||
    w.kind === 'pdf' ||
    w.kind === 'gdoc' ||
    w.kind === 'gsheet' ||
    w.kind === 'gslide' ||
    w.kind === 'email'
  ) {
    return <SharedLink raw={w.content} />
  }

  // Color widget — show the swatch, but only for a value that is a colour and
  // nothing else (a "colour" of url(...) would make every viewer fetch it).
  const swatch = w.kind === 'color' ? safeCssColor(w.content) : null
  if (swatch) {
    return (
      <div className="flex items-center gap-2">
        <div
          className="h-12 w-12 rounded-md border border-white/[0.08]"
          style={{ backgroundColor: swatch }}
        />
        <code className="text-[11px] font-mono text-stone-300">{w.content}</code>
      </div>
    )
  }

  // Image — only from a source an image may safely load from.
  const imageSrc = w.kind === 'image' ? safeImageSrc(w.content) : null
  if (imageSrc) {
    return (
      <img
        src={imageSrc}
        alt={w.title || 'shared image'}
        className="max-w-full max-h-64 rounded"
        loading="lazy"
        referrerPolicy="no-referrer"
      />
    )
  }

  // Catch-all — render the content as preformatted text if present.
  if (w.content) {
    return (
      <div className="whitespace-pre-wrap text-[12px] text-stone-300">
        {w.content.slice(0, 600)}
        {w.content.length > 600 && (
          <span className="text-stone-500"> …</span>
        )}
      </div>
    )
  }

  return (
    <div className="text-[12px] text-stone-500 italic">(no preview available)</div>
  )
}

// A link written by whoever shared this. It is a link only if safeHref accepts
// it (absolute http, https or mailto); anything else -- javascript:, data:, a
// relative path, a local file -- is shown as the text it is, and is not
// clickable. The visible text is the address the link actually opens, so what
// a reader sees is where they go.
function SharedLink({ raw }: { raw: string }): JSX.Element {
  const href = safeHref(raw)
  if (!href) {
    const text = (raw || '').trim()
    return (
      <div className="text-[12px]">
        <div className="text-stone-500 mb-1">Not a web or email address, so shown as text:</div>
        <span className="text-stone-300 break-all font-mono text-[11px]">
          {text ? (text.length > 300 ? `${text.slice(0, 300)}…` : text) : '(empty)'}
        </span>
      </div>
    )
  }
  return (
    <div className="text-[12px]">
      <div className="text-stone-500 mb-1">Link to:</div>
      <a href={href} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline break-all">
        {href}
      </a>
    </div>
  )
}

function TablePreview({ t }: { t: SerializedTable }): JSX.Element {
  const shown = t.rows.slice(0, 20)
  const unnamed = unnamedColumns(t.columns, shown)
  return (
    <div className="overflow-x-auto">
      {t.title && (
        <div className="text-[12px] font-medium text-stone-200 mb-1.5">{t.title}</div>
      )}
      <table className="w-full text-[11px] border-collapse">
        <thead>
          <tr className="border-b border-white/[0.08]">
            {t.columns.map((c) => (
              <th
                key={c.id}
                className="text-left px-2 py-1 text-stone-400 font-medium"
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {shown.map((r) => (
            <tr key={r.id} className="border-b border-white/[0.04]">
              {t.columns.map((c) => {
                const cell = cellText(r.cells?.[c.id], c)
                return (
                  <td
                    key={c.id}
                    className={`px-2 py-1 ${cell.unnamed ? 'text-stone-500' : 'text-stone-200'}`}
                    title={cell.unnamed ? 'This link does not include the name of this choice' : undefined}
                  >
                    {cell.text}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {t.rows.length > 20 && (
        <div className="text-[10px] text-stone-500 mt-1">
          showing 20 of {t.rows.length} rows
        </div>
      )}
      {unnamed.length > 0 && (
        <div className="text-[10px] text-stone-500 mt-1" data-testid="table-unnamed-choices">
          The names of the choices in {listOf(unnamed)} are not part of this link: it was made with an
          older version of Plexii. A new link from the sender will show them.
        </div>
      )}
    </div>
  )
}

function listOf(names: string[]): string {
  if (names.length <= 1) return names.join('')
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

// Tiptap JSON → flat string. Best-effort: walks doc nodes and joins their
// text content. Falls back to the raw content if not parseable.
function extractText(raw: string): string {
  if (!raw) return ''
  const trimmed = raw.trim()
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return raw
  try {
    const doc = JSON.parse(trimmed) as { content?: unknown[]; text?: string }
    return walkText(doc).trim()
  } catch {
    return raw
  }
}

function walkText(node: unknown): string {
  if (!node || typeof node !== 'object') return ''
  const n = node as { type?: string; text?: string; content?: unknown[] }
  if (n.text) return n.text
  if (Array.isArray(n.content)) {
    const sep = n.type === 'paragraph' || n.type === 'heading' ? '\n' : ' '
    return n.content.map(walkText).join('') + sep
  }
  return ''
}

// ── Common chrome ───────────────────────────────────────────────────────

function StatusPill({ status }: { status: string }): JSX.Element {
  const palette: Record<
    string,
    { bg: string; color: string; label: string }
  > = {
    open: { bg: 'rgba(148,163,184,0.10)', color: '#cbd5e1', label: 'Open' },
    in_progress: { bg: 'rgba(96,165,250,0.15)', color: '#93c5fd', label: 'In progress' },
    done: { bg: 'rgba(52,211,153,0.15)', color: '#6ee7b7', label: 'Done' },
    parked: { bg: 'rgba(251,191,36,0.15)', color: '#fcd34d', label: 'Parked' }
  }
  const p = palette[status] || palette.open
  return (
    <span
      className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full font-medium"
      style={{ backgroundColor: p.bg, color: p.color }}
    >
      {p.label}
    </span>
  )
}

function DueChip({ due, done }: { due: number; done: boolean }): JSX.Element {
  const daysLeft = Math.ceil((due - Date.now()) / 86_400_000)
  const label = done
    ? new Date(due).toLocaleDateString()
    : daysLeft < 0
      ? `${-daysLeft}d late`
      : daysLeft === 0
        ? 'today'
        : daysLeft === 1
          ? 'tomorrow'
          : `in ${daysLeft}d`
  const color = done
    ? 'rgba(148,163,184,0.7)'
    : daysLeft < 0
      ? '#fb7185'
      : daysLeft <= 2
        ? '#fcd34d'
        : '#94a3b8'
  return (
    <span
      className="text-[10px] font-mono tabular-nums px-1.5 py-0.5 rounded"
      style={{
        backgroundColor: 'rgba(255,255,255,0.04)',
        color,
        border: '1px solid rgba(255,255,255,0.06)'
      }}
    >
      {label}
    </span>
  )
}
